import { supabase, getProdSupabaseClient } from './client';
import { getTenantIdFromSession, getRecallsForTenant } from './tenantScoping';

/**
 * Get the configured snapshot date for recall data.
 * If no configuration exists, returns null (indicating to use latest available).
 */
export const getConfiguredSnapshotDate = async (): Promise<string | null> => {
  try {
    const prodClient = getProdSupabaseClient();
    const { data, error } = await prodClient
      .from('admin_snapshot_config')
      .select('configured_snapshot_date')
      .eq('tenant_id', 'default')
      .single();

    if (error) {
      // If no configuration exists, return null (use latest)
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    const date = data?.configured_snapshot_date || null;
    if (date) {
      // Convert to date-only format (YYYY-MM-DD) to avoid timezone issues
      const dateOnly = date.includes('T') ? date.split('T')[0] : date;
      return dateOnly;
    }
    return null;
  } catch (error) {
    console.error('Failed to get configured snapshot date:', error);
    // Fail gracefully - return null to use latest available
    return null;
  }
};

/**
 * Get the latest available snapshot date from the recall data.
 * Uses RPC function to avoid direct query to materialized view.
 */
export const getLatestSnapshotDate = async (): Promise<string | null> => {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);

    if (!tenantId) {
      console.warn('[snapshotConfig] No tenant ID available for getLatestSnapshotDate');
      return null;
    }

    // Use RPC function to get tenant-scoped recalls
    const { data, error } = await getRecallsForTenant(prodClient, tenantId, 1, 0);

    if (error) throw error;

    const firstRecord = data?.[0];
    const date = firstRecord?.snapshot_date || null;
    if (date) {
      // Convert to date-only format (YYYY-MM-DD) to avoid timezone issues
      const dateOnly = date.includes('T') ? date.split('T')[0] : date;
      return dateOnly;
    }
    return null;
  } catch (error) {
    console.error('Failed to get latest snapshot date:', error);
    return null;
  }
};

/**
 * Get the effective snapshot date to use for queries.
 * Returns configured date if exists and has data, otherwise latest available.
 * This ensures we always use a date that has actual data in the database.
 */
export const getEffectiveSnapshotDate = async (): Promise<string | null> => {
  const configuredDate = await getConfiguredSnapshotDate();
  const latestDate = await getLatestSnapshotDate();

  // If no configured date, use latest
  if (!configuredDate) {
    return latestDate;
  }

  // If configured date exists, verify it has data
  // If not, fall back to latest (which should always have data)
  try {
    const prodClient = getProdSupabaseClient();

    // Use date range query to handle timestamp format in database
    // Database stores as timestamp (2025-12-16T06:00:00.000Z), we compare with date-only (2025-12-16)
    const dateStart = `${configuredDate}T00:00:00.000Z`;
    const dateEnd = `${configuredDate}T23:59:59.999Z`;

    // Use RPC function to count tenant-scoped recalls
    const tenantId = await getTenantIdFromSession(prodClient);

    if (!tenantId) {
      return latestDate;
    }

    // Get a sample of records to check if configured date has data
    // Note: We can't filter by snapshot_date in RPC, so we get records and check client-side
    const { data: sampleData, error: countError } = await getRecallsForTenant(
      prodClient,
      tenantId,
      100,
      0
    );

    if (countError) {
      // Fall back to latest on error
      return latestDate;
    }

    // Check if any records match the configured date
    const matchingRecords = (sampleData || []).filter((r: any) => {
      const recordDate = r.snapshot_date;
      if (!recordDate) return false;
      return recordDate >= dateStart && recordDate <= dateEnd;
    });

    // If configured date has no data, use latest instead
    if (matchingRecords.length === 0) {
      return latestDate;
    }

    return configuredDate;
  } catch {
    // On error, fall back to latest
    return latestDate;
  }
};

/**
 * Update the configured snapshot date.
 */
export const setConfiguredSnapshotDate = async (
  snapshotDate: string,
  updatedBy?: string
): Promise<void> => {
  // Normalize to date-only format if it's a timestamp
  const dateOnly = snapshotDate.includes('T') ? snapshotDate.split('T')[0] : snapshotDate;

  const prodClient = getProdSupabaseClient();
  const upsertData = {
    tenant_id: 'default',
    configured_snapshot_date: dateOnly, // Store as date-only to avoid timezone issues
    updated_at: new Date().toISOString(),
    updated_by: updatedBy || 'admin'
  };

  const { error } = await prodClient.from('admin_snapshot_config').upsert(upsertData, {
    onConflict: 'tenant_id'
  });

  if (error) {
    throw error;
  }
};

/**
 * Remove the configured snapshot date (revert to using latest).
 */
export const removeConfiguredSnapshotDate = async (): Promise<void> => {
  const { error } = await supabase
    .schema('prod')
    .from('admin_snapshot_config')
    .delete()
    .eq('tenant_id', 'default');

  if (error) throw error;
};

/**
 * Get all available snapshot dates with record counts.
 * Returns dates in date-only format (YYYY-MM-DD) to avoid timezone issues.
 * Uses RPC function to avoid direct query to materialized view.
 */
export const getAvailableSnapshots = async (): Promise<
  Array<{
    snapshot_date: string;
    record_count: number;
  }>
> => {
  try {
    const prodClient = getProdSupabaseClient();
    const tenantId = await getTenantIdFromSession(prodClient);

    if (!tenantId) {
      console.warn('[snapshotConfig] No tenant ID available for getAvailableSnapshots');
      return [];
    }

    // Get tenant-scoped recalls using RPC function (load in batches to get all snapshot dates)
    const PAGE_SIZE = 5000;
    let allData: any[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const { data: pageData, error: pageError } = await getRecallsForTenant(
        prodClient,
        tenantId,
        PAGE_SIZE,
        offset
      );

      if (pageError) {
        console.error('Database query error:', pageError);
        throw pageError;
      }

      if (pageData && pageData.length > 0) {
        allData = [...allData, ...pageData];
        offset += PAGE_SIZE;
        hasMore = pageData.length === PAGE_SIZE;
      } else {
        hasMore = false;
      }
    }

    const data = allData;

    if (!data || data.length === 0) {
      console.warn('No data returned from tenant-scoped recalls');
      return [];
    }

    // Count records per snapshot date, converting to date-only format (YYYY-MM-DD)
    const snapshotCounts = (data || []).reduce(
      (acc, row) => {
        // Convert timestamp to date-only string (YYYY-MM-DD) to avoid timezone issues
        const dateStr = row.snapshot_date;
        let dateOnly: string;

        if (dateStr) {
          // If it's a timestamp, extract just the date part
          if (dateStr.includes('T')) {
            dateOnly = dateStr.split('T')[0];
          } else {
            dateOnly = dateStr;
          }
        } else {
          return acc; // Skip null/undefined dates
        }

        acc[dateOnly] = (acc[dateOnly] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    // Convert to array format, using date-only strings
    const result = Object.entries(snapshotCounts)
      .map(([snapshot_date, record_count]) => ({
        snapshot_date, // This is now YYYY-MM-DD format
        record_count
      }))
      .sort((a, b) => {
        // Sort by date string (YYYY-MM-DD format sorts correctly)
        return b.snapshot_date.localeCompare(a.snapshot_date);
      });

    return result;
  } catch (error) {
    console.error('Failed to get available snapshots:', error);
    return [];
  }
};
